import { generatePath } from 'react-router-dom';

import { AuthenticatedLayoutView } from 'app/components/Layout/AuthenticatedLayout/types';

import NavLinkItems from 'app/constants/navigation';
import { globalPaths, companyPaths } from 'app/constants/routes';
import { RECONCILIATION_TYPE_PARAM, ReconciliationType } from 'app/constants/salaryReconciliations';

import { useManagingCompanyStore, useNavigationStore } from 'app/store';

export function getBlankPath() {
  return globalPaths.companies.path;
}

export function getIntegrationSyncHistoryPath(companyId?: string, syncType?: string) {
  const params = new URLSearchParams();
  if (companyId) params.set('companyId', companyId);
  if (syncType) params.set('syncType', syncType);

  const query = params.toString();
  return query ? `${globalPaths.syncHistory.path}?${query}` : globalPaths.syncHistory.path;
}

export function getMyProfilePath() {
  return globalPaths.profile.path;
}

export function getChangePasswordPath() {
  return globalPaths.changePassword.path;
}

export function getSidebarPaths(view: AuthenticatedLayoutView, companyId?: string) {
  if (view === 'company') {
    const id = companyId ?? useManagingCompanyStore.getState().company?.id;

    // Company paths can't be generated without an id (e.g. before the company has
    // loaded). Return nothing rather than letting generatePath throw.
    if (!id) {
      return [];
    }

    return NavLinkItems.company.map((link) => {
      return {
        ...link,
        path: generatePath(link.path, { companyId: id }),
      };
    });
  }

  return NavLinkItems.global;
}

export function getSelectedCompanyPath(companyId: string) {
  return generatePath(companyPaths.employees.path, { companyId });
}

export function getSelectedCompanyDashboardPath(companyId: string) {
  return generatePath(companyPaths.dashboard.path, { companyId });
}

export function getCompanyDashboardPath() {
  const { company } = useManagingCompanyStore.getState();

  return generatePath(companyPaths.dashboard.path, { companyId: company?.id });
}

export function getHomePath() {
  const { view } = useNavigationStore.getState();
  const { company } = useManagingCompanyStore.getState();

  if (view === 'company') {
    return generatePath(companyPaths.home.path, { companyId: company?.id });
  }

  return globalPaths.companies.path;
}

export function getAdminsPath() {
  const { view } = useNavigationStore.getState();
  const { company } = useManagingCompanyStore.getState();

  if (view === 'company') {
    return generatePath(companyPaths.admins.path, { companyId: company?.id });
  }

  return globalPaths.admins.path;
}

export function getAdminProfilePath(individualId: string) {
  const { view } = useNavigationStore.getState();
  const { company } = useManagingCompanyStore.getState();

  if (view === 'company')
    return generatePath(companyPaths.adminProfile.path, {
      companyId: company?.id,
      individualId,
    });

  return generatePath(globalPaths.adminProfile.path, { individualId });
}

export function getEmployeesPath() {
  const { view } = useNavigationStore.getState();
  const { company } = useManagingCompanyStore.getState();

  if (view === 'company')
    return generatePath(companyPaths.employees.path, { companyId: company?.id });

  return globalPaths.employees.path;
}

export function getEmployeeProfilePath(individualId: string, companyId?: string) {
  const { view } = useNavigationStore.getState();
  const { company } = useManagingCompanyStore.getState();
  const resolvedCompanyId = companyId ?? company?.id;

  if (view === 'company' && resolvedCompanyId)
    return generatePath(companyPaths.employeeProfile.path, {
      companyId: resolvedCompanyId,
      individualId,
    });

  if (companyId)
    return generatePath(companyPaths.employeeProfile.path, {
      companyId,
      individualId,
    });

  return generatePath(globalPaths.employeeProfile.path, { individualId });
}

export function getPayCalendarsPath(companyId = '') {
  const { company } = useManagingCompanyStore.getState();

  return generatePath(companyPaths.payCalendars.path, {
    companyId: company?.id || companyId || '',
  });
}

export function getPayCalendarProfilePath(payCalendarId: string, companyId = '') {
  const { view } = useNavigationStore.getState();
  const { company } = useManagingCompanyStore.getState();

  return generatePath(companyPaths.payCalendarProfile.path, {
    companyId: view === 'company' ? company?.id || companyId : companyId,
    payCalendarId,
  });
}

export function getPayrollReportsPath() {
  const { view } = useNavigationStore.getState();
  const { company } = useManagingCompanyStore.getState();

  if (view === 'company') {
    return generatePath(companyPaths.payrollReports.path, { companyId: company?.id });
  }

  return globalPaths.payrollReports.path;
}

export function getImportEmployeesPath() {
  const { view } = useNavigationStore.getState();
  const { company } = useManagingCompanyStore.getState();

  if (view === 'company')
    return generatePath(companyPaths.importEmployees.path, { companyId: company?.id });

  return globalPaths.importEmployees.path;
}

export function getNotificationsPath() {
  const { company } = useManagingCompanyStore.getState();

  return generatePath(companyPaths.notifications.path, { companyId: company?.id });
}

export function getGiftingPath() {
  const { company } = useManagingCompanyStore.getState();

  return generatePath(companyPaths.gifting.path, { companyId: company?.id });
}

/** The gift flow for a company; with a gift id, it edits that scheduled gift instead of making a new one. */
export function getRewardEmployeesPath(companyId: string, editGiftId?: string) {
  const path = generatePath(companyPaths.rewardEmployees.path, { companyId });

  return editGiftId ? `${path}?editGiftId=${editGiftId}` : path;
}

/** The gifting page, opened on its scheduled gifts tab. */
export function getScheduledGiftsPath(companyId: string) {
  return `${generatePath(companyPaths.gifting.path, { companyId })}?tab=scheduled`;
}

export function getSettingsPath() {
  const { company } = useManagingCompanyStore.getState();

  return generatePath(companyPaths.settings.path, { companyId: company?.id });
}

export function getIndividualDeductionsReportProfilePath(individualDeductionsReportId: string) {
  const { view } = useNavigationStore.getState();
  const { company } = useManagingCompanyStore.getState();

  if (view === 'company')
    return generatePath(companyPaths.individualDeductionsReportProfile.path, {
      companyId: company?.id,
      individualDeductionsReportId,
    });

  return generatePath(globalPaths.individualDeductionsReportProfile.path, {
    individualDeductionsReportId,
  });
}

export function getReconciliationsPath(type?: ReconciliationType) {
  const { view } = useNavigationStore.getState();
  const { company } = useManagingCompanyStore.getState();
  const query = type === 'salary' ? `?${RECONCILIATION_TYPE_PARAM}=${type}` : '';

  if (view === 'company' && company?.id) {
    return `${generatePath(companyPaths.reconciliations.path, { companyId: company.id })}${query}`;
  }

  return `${globalPaths.reconciliations.path}${query}`;
}

export function getCompanySalaryReconciliationProfilePath(
  companyId: string,
  reconciliationId: string
) {
  return generatePath(companyPaths.salaryReconciliationProfile.path, {
    companyId,
    reconciliationId,
  });
}

export function getSalaryReconciliationProfilePath(reconciliationId: string) {
  const { view } = useNavigationStore.getState();
  const { company } = useManagingCompanyStore.getState();

  if (view === 'company' && company?.id) {
    return getCompanySalaryReconciliationProfilePath(company.id, reconciliationId);
  }

  return generatePath(globalPaths.salaryReconciliationProfile.path, { reconciliationId });
}

export function getReconciliationProfilePath(reconciliationId: string) {
  const { view } = useNavigationStore.getState();
  const { company } = useManagingCompanyStore.getState();

  if (view === 'company' && company?.id) {
    return generatePath(companyPaths.reconciliationProfile.path, {
      companyId: company.id,
      reconciliationId,
    });
  }

  return generatePath(globalPaths.reconciliationProfile.path, { reconciliationId });
}

export function getCompanyReconciliationProfilePath(companyId: string, reconciliationId: string) {
  return generatePath(companyPaths.reconciliationProfile.path, { companyId, reconciliationId });
}

export function getDeductionsReportProfilePath(deductionsReportId: string) {
  const { view } = useNavigationStore.getState();
  const { company } = useManagingCompanyStore.getState();

  if (view === 'company' && company?.id) {
    return generatePath(companyPaths.companyDeductionsReportProfile.path, {
      companyId: company?.id,
      deductionsReportId,
    });
  }

  return generatePath(globalPaths.companyDeductionsReportProfile.path, {
    deductionsReportId,
  });
}
